import {
  type GetApiAdminCompanyByCompanyTreeSettingsIdResponse,
  useGetCompanyById,
} from '@sit/client-shared';
import { Card, CardColumn, CardRow } from 'carbon-react/lib/components/card';
import Divider from 'carbon-react/lib/components/divider';
import Typography from 'carbon-react/lib/components/typography';
import styled from 'styled-components';
import { useCompanyTreeSettingsIdInPathParams } from '../helpers';
import { CheckCompanyPermissions } from './CheckCompanyPermissions';
import { CompanyActivationCard } from './CompanyActivationCard';
import { CompanyDetailsForm } from './CompanyDetailsForm';
import SyncStatusForm from './CompanySyncStatus';
import { UpdateWSUForm } from './UpdateWSUForm';

const FullWidthRoot = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: 6rem;
`;

const FirstGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto auto;
  gap: 16px 16px;
  grid-auto-flow: row;
  grid-template-areas:
    'details sync .'
    'details permissions .'
    'details activity .'
    '. . .';
`;

const DetailsGridArea = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SyncGridArea = styled.div`
  grid-area: sync;
`;

const PermissionsGridArea = styled.div`
  grid-area: permissions;
`;

const ActivityGridArea = styled.div`
  grid-area: activity;
`;

export interface CompanyDetailsPageProps {
  company: GetApiAdminCompanyByCompanyTreeSettingsIdResponse;
}

// Known bug: when you reload the page, it performs the SAML authentication again.
// Upon re-navigating to this page at /companies/1, it instead navigates to /companies/:id.
// That's why there's a check in useCompanyTreeSettingsIdInPathParams to ensure that it's an integer.
export function CompanyDetailsPage() {
  const companyTreeSettingsId = useCompanyTreeSettingsIdInPathParams();
  const { data: company, isLoading } = useGetCompanyById({
    companyTreeSettingsId,
  });

  if (isLoading || !company) {
    return <FullWidthRoot>Loading...</FullWidthRoot>;
  }

  return (
    <FullWidthRoot>
      <FirstGrid>
        <DetailsGridArea>
          <CompanyDetailsForm company={company} />
          <UpdateWSUForm intacctCompanyId={company.externalId} />
        </DetailsGridArea>
        <SyncGridArea>
          <Card>
            <CardRow pb={0}>
              <CardColumn align="left">
                <Typography variant="h1" mt={2}>
                  Intacct sync status
                </Typography>
              </CardColumn>
            </CardRow>
            <Divider type="horizontal" mt={2} mb={2} />
            <SyncStatusForm
              companyTreeSettingsId={companyTreeSettingsId}
              intacctCompanyId={company.externalId}
              dataSyncActive={company.dataSyncActive}
            />
          </Card>
        </SyncGridArea>
        <PermissionsGridArea>
          <Card>
            <CardRow pb={0}>
              <CardColumn align="left">
                <Typography variant="h1" mt={2}>
                  Check company permissions
                </Typography>
              </CardColumn>
            </CardRow>
            <Divider type="horizontal" mt={2} mb={2} />
            <CardRow>
              <CardColumn align="right">
                <CheckCompanyPermissions
                  intacctCompanyId={company.externalId}
                  companyTreeSettingsId={companyTreeSettingsId}
                />
              </CardColumn>
            </CardRow>
          </Card>
        </PermissionsGridArea>
        <ActivityGridArea>
          <CompanyActivationCard
            adminEmail={company.adminEmail}
            adminUsername={company.adminUsername}
            archivedOn={company.archivedOn}
            companyTreeSettingsId={companyTreeSettingsId}
            intacctCompanyId={company.externalId}
            reactivatedOn={company.reactivatedOn}
          />
        </ActivityGridArea>
      </FirstGrid>
    </FullWidthRoot>
  );
}
